// GamesHub — U3 (2026-07-09). The unified "Games" tab.
//
// Replaces the Cricket-only bottom-nav slot (owner directive: cricket is
// the main game but not the ONLY game — agents also earn from casino /
// small games and had no page showing it anywhere).
//
// One question per screen: "What did each game earn me?"
//   * Hero    — all games combined, for the picked range.
//   * Main game — Cricket card (isolated book) → /games/cricket detail.
//   * Casino & small games — one plain row per registry game.
//
// Registry-driven: new games appear automatically with their display
// name + tag from the `games` table (via /admin/me-agent/games-summary).
// UI doctrine (docs/AGENT_UI_SIMPLIFICATION_REPORT.md): plain words,
// both themes, no JS-gated visibility, tertiary text ≥ /60 in dark.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import HubHeader from '@baaligames/shared-ui/admin/components/HubHeader';
import HeroCard from '@baaligames/shared-ui/admin/components/HeroCard';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { T } from '@baaligames/shared-ui/admin/lib/textStyles';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useDownlineLabel } from '@baaligames/shared-ui/admin/lib/downlineLabel';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type Range = 'today' | 'week' | 'month' | 'all';
type Summary = {
  range: string;
  total: number;
  returnTotal?: number;
  returnPct?: number | null;
  cricket: {
    earned: number; turnoverShare?: number; returnPct?: number | null; entries: number; balance: number;
    rates: { profitSharePct: number; commissionSharePct: number };
  };
  casino: {
    earned: number;
    returnTotal?: number;
    games: Array<{ gameId: string; name: string; tag: string | null; locked: boolean; earned: number; turnoverShare?: number; returnPct?: number | null }>;
  };
};

// RC (owner 2026-07-10): the turnover RETURN gets ONE unique color — orange —
// everywhere on the broker end, in both themes, so it always reads as "the
// extra thing on top", never blending into win/loss partnership money.
const RETURN_TONE = 'text-orange-600 dark:text-orange-400';

// The Return label + its rate bracket, e.g. "Return[2%]". The bracket rate is
// the CHAIN's DIFFERENCE share of turnover (X − Y — full reasoning single-homed
// in commissionConfig.js chainReturnPct); it comes ready-made from the backend
// as `returnPct`, and is null when the view spans categories with different
// rates — then the label renders without a bracket rather than lie with one
// number. (The equation lines below show a DIFFERENT % — the agent's own slice
// of the pool — so they keep their explicit base × % = cut form instead.)
const retBadge = (label: string, pct?: number | null) =>
  pct != null ? `${label}[${pct}%]` : label;

const inr = (n: number) => `₹${Math.abs(n).toLocaleString('en-IN')}`;
const signed = (n: number) => (n < 0 ? `−${inr(n)}` : `+${inr(n)}`);
// A BALANCE must keep its sign. inr() strips it (right for signed(), which re-adds
// one), so using inr() bare on the cricket wallet made a NEGATIVE balance read as
// positive — a broker who was DOWN ₹1,054 saw "Cricket wallet ₹1,054". No leading
// '+' here: a plain balance reads naturally without it.
const inrBalance = (n: number) => (n < 0 ? `−${inr(n)}` : inr(n));
const moneyTone = (n: number) =>
  n > 0 ? 'text-emerald-600 dark:text-emerald-300'
  : n < 0 ? 'text-rose-600 dark:text-rose-300'
  : 'text-arena-500 dark:text-neon-200/60';

// Per-game icon — falls back to a controller for anything unmapped so a
// future registry game never renders iconless.
const GAME_ICONS: Record<string, string> = {
  ludo: 'casino', xo: 'grid_3x3', rummy: 'playing_cards', chess: 'chess',
  carrom: 'radio_button_checked', lucky7: 'counter_7', snake_ladder: 'stairs',
  plinko: 'blur_on', thimbles: 'search', aviator: 'flight_takeoff',
  teen_patti: 'playing_cards', andar_bahar: 'style', gems_mine: 'diamond',
  roulette: 'casino', casino: 'casino',
};

export default function GamesHub() {
  const t = useT();
  const [range, setRange] = useState<Range>('all');
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState(false);
  // "Who caused it" modal — owner request 2026-07-09: tapping the hero,
  // the cricket card or any game row opens the per-person breakdown.
  const [detail, setDetail] = useState<{ game: string; name: string } | null>(null);
  // Hisaab view selector — owner request 2026-07-09: the page is the
  // agent's full statement, switchable between Game Hisaab (by game)
  // and People Hisaab (by downline + direct players).
  const [view, setView] = useState<'game' | 'people'>('game');

  useEffect(() => {
    let cancelled = false;
    setErr(false);
    adminApi.get<Summary>(`/admin/me-agent/games-summary?range=${range}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [range]);

  const RANGES: Array<{ key: Range; label: string }> = [
    { key: 'today', label: t('agent.games.range.today') },
    { key: 'week',  label: t('agent.games.range.week') },
    { key: 'month', label: t('agent.games.range.month') },
    { key: 'all',   label: t('agent.games.range.all') },
  ];
  const rangeLabel = RANGES.find((r) => r.key === range)?.label ?? '';

  // Casino rows — games with money first (biggest movement on top),
  // then the quiet ones in registry order.
  const rows = data
    ? [...data.casino.games].sort((a, b) => Math.abs(b.earned) - Math.abs(a.earned))
    : [];
  const allQuiet = rows.length > 0 && rows.every((g) => g.earned === 0);

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      <HubHeader
        icon="receipt_long" tone="violet"
        title={t('agent.games.hub.title')}
        subtitle={t('agent.games.hub.subtitle')}
      />

      {/* Range picker — 4 chips only (owner: 7 was too many). */}
      <div className="px-2 lg:px-4">
        <div className="flex gap-1.5 rounded-2xl p-1.5 bg-arena-100/60 dark:bg-white/[0.05] ring-1 ring-inset ring-arena-200/50 dark:ring-white/10">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`flex-1 min-h-[38px] rounded-xl text-[12.5px] font-bold transition
                          ${range === r.key
                            ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.45)]'
                            : `${T.tertiary} hover:bg-white/60 dark:hover:bg-white/10`}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* View selector — Game Hisaab / People Hisaab (same segmented-pill
          family as the player-end profile selector). */}
      <div className="px-2 lg:px-4">
        <MagentaTabBar
          size="lg"
          accent="violet"
          activeKey={view}
          onChange={(k) => setView(k as 'game' | 'people')}
          items={[
            { key: 'game',   label: t('agent.hisaab.tab.game'),   icon: 'sports_esports' },
            { key: 'people', label: t('agent.hisaab.tab.people'), icon: 'group' },
          ]}
        />
      </div>

      {view === 'people' ? (
        <div className="px-2 lg:px-4">
          <PeopleHisaab range={range} />
        </div>
      ) : (
      <div className="px-2 lg:px-4 space-y-4">
        {err && (
          <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
            {t('agent.games.error')}
          </div>
        )}

        {!err && !data && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-arena-100/70 dark:bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Hero — everything combined. Tapping opens the who-caused-it
                breakdown for ALL games in the picked period. */}
            <button type="button" onClick={() => setDetail({ game: 'all', name: t('agent.games.detail.allGames') })}
                    className="block w-full text-left active:scale-[0.995] transition">
              <HeroCard
                tone="violet" icon="sports_esports"
                kicker={t('agent.games.hero.kicker')}
                value={signed(data.total)}
                sub={<>{t('agent.games.hero.sub')} <span className="font-bold text-white">· {rangeLabel}</span></>}
              >
                {(data.returnTotal ?? 0) !== 0 && (
                  <div className="mt-2 text-[12.5px] font-bold tabular-nums text-white/90">
                    {t('agent.hisaab.pillar.short.win_loss')} {signed(data.total - (data.returnTotal ?? 0))}
                    <span className="text-orange-300"> · {retBadge(t('agent.hisaab.pillar.short.turnover_share'), data.returnPct)} {signed(data.returnTotal!)}</span>
                  </div>
                )}
                <div className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-bold text-white/90">
                  <Icon name="touch_app" size={14} />
                  {t('agent.games.detail.tapHint')}
                </div>
              </HeroCard>
            </button>

            {/* ── Main game — Cricket ─────────────────────────────── */}
            <div>
              <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
                {t('agent.games.section.main')}
              </div>
              <div
                className="rounded-2xl bg-white dark:bg-arena-700/40
                           border border-arena-200/60 dark:border-arena-600/40
                           shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                           hover:ring-2 hover:ring-emerald-400/50 transition overflow-hidden"
              >
                {/* Card body → who-caused-it modal; footer → full detail page. */}
                <button type="button"
                        onClick={() => setDetail({ game: 'cricket', name: t('agent.games.cricket.name') })}
                        className="w-full text-left flex items-center gap-3 p-4">
                  <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0
                                   bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100
                                   dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30">
                    <Icon name="sports_cricket" size={24} filled />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[15px] font-bold ${T.primary}`}>{t('agent.games.cricket.name')}</div>
                    <div className={`text-[12px] mt-0.5 ${T.tertiary}`}>
                      {t('agent.games.cricket.balance')} <span className="font-bold">{inrBalance(data.cricket.balance)}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-black text-[20px] tabular-nums ${moneyTone(data.cricket.earned)}`}>
                      {data.cricket.earned === 0 ? '—' : signed(data.cricket.earned)}
                    </div>
                    {(data.cricket.turnoverShare ?? 0) !== 0 && (
                      <div className={`text-[10.5px] font-bold tabular-nums ${RETURN_TONE}`}>
                        {retBadge(t('agent.hisaab.pillar.short.turnover_share'), data.cricket.returnPct)} {signed(data.cricket.turnoverShare!)}
                      </div>
                    )}
                    <div className={`text-[10.5px] ${T.faint}`}>{rangeLabel}</div>
                  </div>
                  <Icon name="chevron_right" size={18} className="text-arena-400 dark:text-neon-200/60 shrink-0" />
                </button>
                <Link
                  to="/games/cricket"
                  className="flex items-center justify-between px-4 py-2.5
                             border-t border-arena-100 dark:border-white/8
                             text-[12.5px] font-bold text-emerald-700 dark:text-emerald-300"
                >
                  {t('agent.games.cricket.details')}
                  <Icon name="arrow_forward" size={16} />
                </Link>
              </div>
            </div>

            {/* ── Matka King (2026-09-19): own page — share, partnership, players' hisaab ── */}
            <div>
              <Link to="/games/matka_king"
                className="flex items-center gap-3 rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-4 py-3 active:scale-[.99]">
                <div className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/25 to-yellow-600/15 text-amber-700 dark:text-amber-300"><Icon name="casino" size={22} filled /></div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[15px] font-bold ${T.primary}`}>Matka King</div>
                  <div className={`text-[11.5px] ${T.secondary}`}>Your share, partnership ladder, players' hisaab</div>
                </div>
                <Icon name="chevron_right" size={18} className="text-arena-400 dark:text-neon-200/60 shrink-0" />
              </Link>
            </div>

            {/* ── Casino & small games ────────────────────────────── */}
            <div>
              <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
                {t('agent.games.section.casino')}
              </div>
              <div className="rounded-2xl bg-white dark:bg-arena-700/40
                              border border-arena-200/60 dark:border-arena-600/40
                              shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                              divide-y divide-arena-100 dark:divide-white/8">
                {rows.map((g) => (
                  <button key={g.gameId} type="button"
                          onClick={() => setDetail({ game: g.gameId, name: g.name })}
                          className="w-full text-left flex items-center gap-3 px-4 py-3
                                     hover:bg-arena-50/60 dark:hover:bg-white/[0.04] transition">
                    <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                                     bg-violet-50 text-violet-700 ring-1 ring-violet-100
                                     dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
                      <Icon name={GAME_ICONS[g.gameId] || 'sports_esports'} size={18} filled />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[14px] font-bold ${T.primary}`}>{g.name}</div>
                      {g.tag && <div className={`text-[11px] ${T.faint}`}>{g.tag}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-black text-[16px] tabular-nums ${moneyTone(g.earned)}`}>
                        {g.earned === 0
                          ? <span className={`text-[11.5px] font-semibold ${T.faint}`}>{t('agent.games.row.nothing')}</span>
                          : signed(g.earned)}
                      </div>
                      {(g.turnoverShare ?? 0) !== 0 && (
                        <div className={`text-[10px] font-bold tabular-nums ${RETURN_TONE}`}>
                          {retBadge(t('agent.hisaab.pillar.short.turnover_share'), g.returnPct)} {signed(g.turnoverShare!)}
                        </div>
                      )}
                    </div>
                    <Icon name="chevron_right" size={16} className="text-arena-400 dark:text-neon-200/60 shrink-0" />
                  </button>
                ))}
              </div>
              {allQuiet && (
                <p className={`px-1 pt-2 text-[12px] ${T.tertiary}`}>
                  {t('agent.games.empty.casino')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
      )}

      {detail && (
        <WhoCausedItModal
          game={detail.game}
          name={detail.name}
          range={range}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

// ─── "Who caused it" modal — owner request 2026-07-09 ────────────────
// For the chosen game + period: the concrete from→to dates in plain
// words, then who produced the money — own players first, then the
// direct downline (level-aware: a Super Master reads "Through your
// Masters", a Master reads "Through your Agents").

type Part = { pillar: string; pct: number | null; cut: number; base?: number };
type Detail = {
  range: string; game: string;
  from: string | null; to: string;
  total: number;
  returnPct?: number | null;
  players: Array<{ userId: number; name: string; amount: number; parts: Part[] }>;
  downline: Array<{ agentId: number; name: string; amount: number; parts: Part[] }>;
  other: number;
};

const fmtDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Sum the Return (turnover rebate) portion out of a pillar-part list — used to
// split every total into "W/L · Return" (the Return in orange).
const returnCut = (parts?: Part[]) =>
  (parts || [])
    .filter((p) => p.pillar === 'turnover_share' || p.pillar === 'turnover_reward')
    .reduce((a, p) => a + p.cut, 0);

// The two-number breakdown line under a total: W/L part + Return part (orange,
// with its [rate%] bracket when the backend can name one honest rate).
// Rendered ONLY when a Return exists — zero-Return views stay exactly as before.
function SplitLine({ total, ret, retPct, className = '' }: { total: number; ret: number; retPct?: number | null; className?: string }) {
  const t = useT();
  if (ret === 0) return null;
  return (
    <div className={`text-[11.5px] font-bold tabular-nums ${className}`}>
      <span className={T.tertiary}>{t('agent.hisaab.pillar.short.win_loss')} </span>
      <span className={T.secondary}>{signed(total - ret)}</span>
      <span className={RETURN_TONE}> · {retBadge(t('agent.hisaab.pillar.short.turnover_share'), retPct)} {signed(ret)}</span>
    </div>
  );
}

// Pillar → short chip label key (compact, for the % line).
const PILLAR_SHORT: Record<string, string> = {
  win_loss: 'agent.hisaab.pillar.short.win_loss',
  profit_share: 'agent.hisaab.pillar.short.profit_share',
  commission_share: 'agent.hisaab.pillar.short.commission_share',
  adjust: 'agent.hisaab.pillar.short.adjust',
  turnover_share: 'agent.hisaab.pillar.short.turnover_share',
};

// The % line under a name (owner corrections 2026-07-09 ×2): ONE money-word
// for the whole page — "मेरा कट" — and lists carry ONE percentage only.
//   compact (lists)      → "मेरा कट 10%"  (the main/win-loss rate; if the
//                          person has no win-loss part, the biggest one,
//                          with its short pillar name)
//   full (modal rows)    → non-win-loss parts only, e.g. "मैच 2% · सेशन 2%"
//                          (the headline equation already explains win-loss)
// Amounts NEVER appear here — the one amount sits big on the right.
function PctLine({ parts, mode }: { parts?: Part[]; mode: 'compact' | 'full' }) {
  const t = useT();
  if (!parts || parts.length === 0) return null;
  if (mode === 'compact') {
    const wl = parts.find((p) => p.pillar === 'win_loss');
    const main = wl || parts[0];
    if (main.pct == null) return null;
    return (
      <div className={`text-[11px] leading-snug mt-0.5 font-semibold ${T.tertiary}`}>
        {t('agent.hisaab.person.myCut')} <b className={T.secondary}>{main.pct}%</b>
        {!wl && <span className={T.faint}> · {t(PILLAR_SHORT[main.pillar] || '') || main.pillar}</span>}
      </div>
    );
  }
  const rest = parts.filter((p) => p.pillar !== 'win_loss' && p.pct != null);
  if (rest.length === 0) return null;
  return (
    <div className={`text-[11px] leading-snug mt-0.5 font-semibold ${T.tertiary}`}>
      {rest.map((p, i) => (
        <span key={i} className="whitespace-nowrap">
          {i > 0 && <span className={T.faint}> · </span>}
          {t(PILLAR_SHORT[p.pillar] || '') || p.pillar}
          <b className={T.secondary}> {p.pct}%</b>
        </span>
      ))}
    </div>
  );
}

// The full-story line (owner's own format, 2026-07-09): each earning stream
// shows WHAT happened × at WHAT rate = the cut — so "पक्की कमाई" is never an
// unexplained number:
//    हार-जीत · हारा ₹3,000 × 25% = +₹750
//    कैसीनो · कारोबार ₹10,000 × 2% = +₹200
// Result streams (win/loss, P&L) label the base हारा/जीता by its sign;
// turnover streams label it कारोबार. Cricket bases are derived from
// cut ÷ rate (its ledger stores no base) — exact for whole-rupee cuts.
function EquationLines({ parts }: { parts?: Part[] }) {
  const t = useT();
  if (!parts || parts.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {parts.map((p, i) => {
        const isResult = p.pillar === 'win_loss' || p.pillar === 'profit_share';
        const isReturn = p.pillar === 'turnover_share' || p.pillar === 'turnover_reward';
        const base = p.base != null && p.base !== 0
          ? Number(p.base)
          : (p.pct ? Math.round((p.cut / p.pct) * 100) : 0);
        const baseLabel = isResult
          ? (base >= 0 ? t('agent.hisaab.eq.lost') : t('agent.hisaab.eq.won'))
          : t('agent.hisaab.eq.turnover');
        return (
          <div key={i} className={`text-[11px] leading-snug font-semibold tabular-nums ${T.tertiary}`}>
            <span className={isReturn ? RETURN_TONE : ''}>{t(PILLAR_SHORT[p.pillar] || '') || p.pillar}</span>
            <span className={T.faint}> · </span>
            {p.pct != null && base !== 0 ? (
              <>
                {baseLabel} <b className={T.secondary}>₹{Math.abs(base).toLocaleString('en-IN')}</b>
                {' × '}<b className={T.secondary}>{p.pct}%</b>{' = '}
              </>
            ) : null}
            <b className={isReturn ? RETURN_TONE : moneyTone(p.cut)}>{signed(p.cut)}</b>
          </div>
        );
      })}
    </div>
  );
}

// The equation card (owner request 2026-07-09): the broker must SEE where
// their cut comes from — three labeled numbers, readable in any language:
//    मेरा रेट 25%  −  इसका रेट 15%  =  मेरा कट 10%
function RateEquation({ my, their }: { my: number; their: number | null }) {
  const t = useT();
  const cell = (label: string, value: string, strong = false) => (
    <div className="text-center min-w-0">
      <div className={`font-black tabular-nums ${strong ? 'text-[22px] text-emerald-600 dark:text-emerald-300' : `text-[20px] ${T.primary}`}`}>
        {value}
      </div>
      <div className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${T.faint}`}>{label}</div>
    </div>
  );
  const op = (ch: string) => (
    <div className={`font-black text-[20px] ${T.tertiary}`}>{ch}</div>
  );
  return (
    <div className="rounded-2xl px-4 py-3 bg-arena-50/80 dark:bg-white/[0.05]
                    ring-1 ring-inset ring-arena-200/50 dark:ring-white/10
                    flex items-center justify-center gap-3 sm:gap-5">
      {their != null ? (
        <>
          {cell(t('agent.hisaab.rate.my'), `${my}%`)}
          {op('−')}
          {cell(t('agent.hisaab.rate.their'), `${their}%`)}
          {op('=')}
          {cell(t('agent.hisaab.person.myCut'), `${Math.round((my - their) * 100) / 100}%`, true)}
        </>
      ) : (
        cell(t('agent.hisaab.person.myCut'), `${my}%`, true)
      )}
    </div>
  );
}

// One person, one amount — shared by the modal and the People Hisaab view.
// With onClick it renders as a tappable row (chevron included).
function PersonRow({ icon, label, chip, amount, ret = 0, retPct, sub, onClick }: {
  icon: string; label: React.ReactNode; chip?: string; amount: number;
  ret?: number; retPct?: number | null; sub?: React.ReactNode; onClick?: () => void;
}) {
  const t = useT();
  const inner = (
    <>
      <span className="grid place-items-center w-8 h-8 rounded-lg shrink-0
                       bg-arena-100/70 text-arena-600 dark:bg-white/[0.07] dark:text-neon-200/75">
        <Icon name={icon} size={16} filled />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-[13.5px] font-bold truncate ${T.primary}`}>
          {label}
          {chip && (
            <span className="ml-2 align-middle inline-flex items-center rounded-full px-2 py-0.5
                             text-[9.5px] font-extrabold uppercase tracking-wider
                             bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
              {chip}
            </span>
          )}
        </div>
        {sub}
      </div>
      <div className="text-right shrink-0">
        <div className={`font-black text-[15px] tabular-nums ${moneyTone(amount)}`}>{signed(amount)}</div>
        {ret !== 0 && (
          <div className={`text-[10px] font-bold tabular-nums ${RETURN_TONE}`}>
            {retBadge(t('agent.hisaab.pillar.short.turnover_share'), retPct)} {signed(ret)}
          </div>
        )}
      </div>
      {onClick && <Icon name="chevron_right" size={16} className="text-arena-400 dark:text-neon-200/60 shrink-0" />}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5
                         hover:bg-arena-50/60 dark:hover:bg-white/[0.04] transition">
        {inner}
      </button>
    );
  }
  return <div className="flex items-center gap-3 px-4 py-2.5">{inner}</div>;
}

// ─── Person → games modal — owner request 2026-07-09 ─────────────────
// The reverse drill-down: pick a person, see which GAME produced my cut
// through them, each game broken into pillar parts with the % snapshot.

type PersonDetail = {
  type: string; id: number; name: string; designation: string | null;
  from: string | null; to: string; total: number;
  returnPct?: number | null;
  games: Array<{ gameId: string; name: string; cut: number; parts: Part[] }>;
  rates: { myWinLossPct: number; theirWinLossPct: number | null };
};

function PersonGamesModal({ type, id, name, designation, range, onClose }: {
  type: 'player' | 'agent'; id: number; name: string; designation?: string | null;
  range: Range; onClose: () => void;
}) {
  const t = useT();
  const [d, setD] = useState<PersonDetail | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi.get<PersonDetail>(`/admin/me-agent/games-summary/person?type=${type}&id=${id}&range=${range}`)
      .then((r) => { if (!cancelled) setD(r); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [type, id, range]);

  return (
    <div className="fixed inset-0 z-[10050] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center sm:p-4"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-lg max-h-[88vh] bg-white dark:bg-arena-900 rounded-t-2xl sm:rounded-2xl
                      flex flex-col shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-arena-200/60 dark:border-white/10 shrink-0">
          <h3 className={`font-bold text-[15px] leading-tight ${T.primary}`}>
            {name}
            {designation && (
              <span className="ml-2 align-middle inline-flex items-center rounded-full px-2 py-0.5
                               text-[9.5px] font-extrabold uppercase tracking-wider
                               bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                {designation}
              </span>
            )}
          </h3>
          <button onClick={onClose} aria-label={t('common.close')}
                  className={`-mr-1 p-2 rounded-full ${T.tertiary} hover:bg-arena-100/60 dark:hover:bg-white/10 active:scale-95`}>
            <Icon name="close" size={22} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {err && <div className="p-4 text-sm text-rose-600 dark:text-rose-300">{t('agent.games.error')}</div>}
          {!err && !d && (
            <div className={`grid place-items-center py-14 text-sm ${T.tertiary}`}>
              <Icon name="progress_activity" size={24} className="animate-spin" />
            </div>
          )}
          {d && (
            <div className="p-4 space-y-4">
              <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5
                              bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300
                              text-[12px] font-bold">
                <Icon name="event" size={14} />
                {t('agent.games.detail.dateLine', { from: fmtDay(d.from), to: fmtDay(d.to) })}
              </div>

              {/* The arithmetic, spelled out — owner correction 2026-07-09:
                  never show a derived % without its inputs. */}
              <RateEquation my={d.rates.myWinLossPct} their={d.rates.theirWinLossPct} />

              <div className="rounded-2xl px-4 py-3
                              bg-arena-50/80 dark:bg-white/[0.05]
                              ring-1 ring-inset ring-arena-200/50 dark:ring-white/10">
                <div className="flex items-center justify-between">
                  <span className={`text-[13px] font-bold ${T.secondary}`}>{t('agent.games.detail.net')}</span>
                  <span className={`font-black text-[22px] tabular-nums ${moneyTone(d.total)}`}>{signed(d.total)}</span>
                </div>
                <SplitLine total={d.total} ret={d.games.reduce((a, g) => a + returnCut(g.parts), 0)} retPct={d.returnPct} className="mt-0.5 text-right" />
              </div>

              {d.games.length === 0 ? (
                <div className={`text-center text-sm py-8 ${T.tertiary}`}>{t('agent.games.detail.empty')}</div>
              ) : (
                <div className="rounded-2xl bg-white dark:bg-arena-700/40
                                border border-arena-200/60 dark:border-arena-600/40
                                divide-y divide-arena-100 dark:divide-white/8 overflow-hidden">
                  {d.games.map((g) => (
                    <div key={g.gameId} className="flex items-start gap-3 px-4 py-3">
                      <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                                       bg-violet-50 text-violet-700 ring-1 ring-violet-100
                                       dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
                        <Icon name={g.gameId === 'cricket' ? 'sports_cricket' : (GAME_ICONS[g.gameId] || 'sports_esports')} size={18} filled />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className={`text-[14px] font-bold ${T.primary}`}>{g.name}</div>
                        <EquationLines parts={g.parts} />
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`font-black text-[16px] tabular-nums ${moneyTone(g.cut)}`}>{signed(g.cut)}</div>
                        <div className={`text-[10px] ${T.faint}`}>{t('agent.hisaab.person.myCut')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* UX Phase 6.2: jump from the earnings breakdown straight to this
            person's full detail page (was a dead-end modal before). */}
        <Link
          to={`/people/${type === 'agent' ? 'agents' : 'players'}/${id}`}
          onClick={onClose}
          className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-3
                     border-t border-arena-200/60 dark:border-white/10
                     text-[13px] font-bold text-violet-700 dark:text-violet-300
                     hover:bg-violet-50 dark:hover:bg-violet-500/10 active:scale-[.99] transition"
        >
          <Icon name="account_circle" size={16} />
          {t('agent.hisaab.person.viewProfile')}
          <Icon name="chevron_right" size={16} />
        </Link>
      </div>
    </div>
  );
}

// ─── People Hisaab — owner request 2026-07-09 ────────────────────────
// The person-wise statement for the picked period: two headline cards
// (through the downline / from direct players), then the two lists with
// designations. Same read-only endpoint as the who-caused-it modal with
// game=all. An L3 Agent can never have a downline, so that card/section
// is hidden entirely for them.

function PeopleHisaab({ range }: { range: Range }) {
  const t = useT();
  const dl = useDownlineLabel();
  const { admin } = useAdminAuth();
  const isLeaf = Number(admin?.level ?? 1) >= 3;
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState(false);
  // Reverse drill-down: tap a person → which games produced my cut.
  const [person, setPerson] = useState<{ type: 'player' | 'agent'; id: number; name: string; designation?: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setD(null); setErr(false);
    adminApi.get<Detail>(`/admin/me-agent/games-summary/detail?game=all&range=${range}`)
      .then((r) => { if (!cancelled) setD(r); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [range]);

  if (err) {
    return (
      <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
        {t('agent.games.error')}
      </div>
    );
  }
  if (!d) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-arena-100/70 dark:bg-white/5 animate-pulse" />
        ))}
      </div>
    );
  }

  const downlineTotal = d.downline.reduce((a, r) => a + r.amount, 0);
  const playersTotal = d.players.reduce((a, r) => a + r.amount, 0);
  const downlineRet = d.downline.reduce((a, r) => a + returnCut(r.parts), 0);
  const playersRet = d.players.reduce((a, r) => a + returnCut(r.parts), 0);
  const isEmpty = d.players.length === 0 && d.downline.length === 0 && d.other === 0;

  const card = 'rounded-2xl p-4 bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none';
  const listShell = 'rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 divide-y divide-arena-100 dark:divide-white/8 overflow-hidden';

  return (
    <div className="space-y-4">
      {/* The concrete period — always spelled out. */}
      <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5
                      bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300
                      text-[12px] font-bold">
        <Icon name="event" size={14} />
        {t('agent.games.detail.dateLine', { from: fmtDay(d.from), to: fmtDay(d.to) })}
      </div>

      {/* Headline cards — downline total + direct players total. */}
      <div className={isLeaf ? '' : 'grid grid-cols-2 gap-3'}>
        {!isLeaf && (
          <div className={card}>
            <div className="flex items-center gap-1.5">
              <Icon name="support_agent" size={15} className="text-violet-600 dark:text-violet-300" />
              <span className={`text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
                {t('agent.games.detail.downline.section', { label: dl.plural })}
              </span>
            </div>
            <div className={`mt-1.5 font-black text-[24px] tabular-nums ${moneyTone(downlineTotal)}`}>
              {signed(downlineTotal)}
            </div>
            <SplitLine total={downlineTotal} ret={downlineRet} retPct={d.returnPct} className="mt-0.5" />
          </div>
        )}
        <div className={card}>
          <div className="flex items-center gap-1.5">
            <Icon name="person" size={15} className="text-violet-600 dark:text-violet-300" />
            <span className={`text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
              {t('agent.hisaab.people.playersCard')}
            </span>
          </div>
          <div className={`mt-1.5 font-black text-[24px] tabular-nums ${moneyTone(playersTotal)}`}>
            {signed(playersTotal)}
          </div>
          <SplitLine total={playersTotal} ret={playersRet} retPct={d.returnPct} className="mt-0.5" />
        </div>
      </div>

      {isEmpty && (
        <div className={`text-center text-sm py-8 ${T.tertiary}`}>{t('agent.games.detail.empty')}</div>
      )}

      {/* Downline list — name + designation + contribution. */}
      {!isLeaf && d.downline.length > 0 && (
        <div>
          <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
            {t('agent.games.detail.downline.section', { label: dl.plural })}
          </div>
          <div className={listShell}>
            {d.downline.map((a) => (
              <PersonRow key={a.agentId} icon="support_agent" chip={dl.singular}
                         label={<>{a.name} <span className={`font-medium ${T.faint}`}>#{a.agentId}</span></>}
                         sub={<PctLine parts={a.parts} mode="compact" />}
                         amount={a.amount} ret={returnCut(a.parts)} retPct={d.returnPct}
                         onClick={() => setPerson({ type: 'agent', id: a.agentId, name: a.name, designation: dl.singular })} />
            ))}
          </div>
        </div>
      )}

      {/* Direct players list. */}
      {d.players.length > 0 && (
        <div>
          <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
            {t('agent.games.detail.players.section')}
          </div>
          <div className={listShell}>
            {d.players.map((p) => (
              <PersonRow key={p.userId} icon="person"
                         label={<>{p.name} <span className={`font-medium ${T.faint}`}>#{p.userId}</span></>}
                         sub={<PctLine parts={p.parts} mode="compact" />}
                         amount={p.amount} ret={returnCut(p.parts)} retPct={d.returnPct}
                         onClick={() => setPerson({ type: 'player', id: p.userId, name: p.name })} />
            ))}
          </div>
        </div>
      )}

      {d.other !== 0 && (
        <div className={listShell}>
          <PersonRow icon="tune" label={t('agent.games.detail.other')} amount={d.other} />
        </div>
      )}

      {person && (
        <PersonGamesModal
          type={person.type} id={person.id} name={person.name} designation={person.designation}
          range={range} onClose={() => setPerson(null)}
        />
      )}
    </div>
  );
}

function WhoCausedItModal({ game, name, range, onClose }: {
  game: string; name: string; range: Range; onClose: () => void;
}) {
  const t = useT();
  const dl = useDownlineLabel();
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi.get<Detail>(`/admin/me-agent/games-summary/detail?game=${encodeURIComponent(game)}&range=${range}`)
      .then((r) => { if (!cancelled) setD(r); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [game, range]);

  const isEmpty = d && d.players.length === 0 && d.downline.length === 0 && d.other === 0;

  return (
    <div className="fixed inset-0 z-[10050] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center sm:p-4"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-lg max-h-[88vh] bg-white dark:bg-arena-900 rounded-t-2xl sm:rounded-2xl
                      flex flex-col shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-arena-200/60 dark:border-white/10 shrink-0">
          <h3 className={`font-bold text-[15px] leading-tight ${T.primary}`}>{name}</h3>
          <button onClick={onClose} aria-label={t('common.close')}
                  className={`-mr-1 p-2 rounded-full ${T.tertiary} hover:bg-arena-100/60 dark:hover:bg-white/10 active:scale-95`}>
            <Icon name="close" size={22} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {err && (
            <div className="p-4 text-sm text-rose-600 dark:text-rose-300">{t('agent.games.error')}</div>
          )}
          {!err && !d && (
            <div className={`grid place-items-center py-14 text-sm ${T.tertiary}`}>
              <Icon name="progress_activity" size={24} className="animate-spin" />
            </div>
          )}
          {d && (
            <div className="p-4 space-y-4">
              {/* The concrete period — owner: "properly describe from which
                  date to which date it is showing the result of". */}
              <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5
                              bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300
                              text-[12px] font-bold">
                <Icon name="event" size={14} />
                {t('agent.games.detail.dateLine', { from: fmtDay(d.from), to: fmtDay(d.to) })}
              </div>

              <p className={`text-[13px] ${T.tertiary}`}>{t('agent.games.detail.desc')}</p>

              {/* Net for the period */}
              <div className="rounded-2xl px-4 py-3
                              bg-arena-50/80 dark:bg-white/[0.05]
                              ring-1 ring-inset ring-arena-200/50 dark:ring-white/10">
                <div className="flex items-center justify-between">
                  <span className={`text-[13px] font-bold ${T.secondary}`}>{t('agent.games.detail.net')}</span>
                  <span className={`font-black text-[22px] tabular-nums ${moneyTone(d.total)}`}>{signed(d.total)}</span>
                </div>
                <SplitLine
                  total={d.total}
                  ret={[...d.players, ...d.downline].reduce((a, r) => a + returnCut(r.parts), 0)}
                  retPct={d.returnPct}
                  className="mt-0.5 text-right"
                />
              </div>

              {isEmpty && (
                <div className={`text-center text-sm py-8 ${T.tertiary}`}>{t('agent.games.detail.empty')}</div>
              )}

              {d.players.length > 0 && (
                <div>
                  <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
                    {t('agent.games.detail.players.section')}
                  </div>
                  <div className="rounded-2xl bg-white dark:bg-arena-700/40
                                  border border-arena-200/60 dark:border-arena-600/40
                                  divide-y divide-arena-100 dark:divide-white/8 overflow-hidden">
                    {d.players.map((p) => (
                      <PersonRow key={p.userId} icon="person"
                                 label={<>{p.name} <span className={`font-medium ${T.faint}`}>#{p.userId}</span></>}
                                 sub={<EquationLines parts={p.parts} />}
                                 amount={p.amount} />
                    ))}
                  </div>
                </div>
              )}

              {d.downline.length > 0 && (
                <div>
                  <div className={`px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
                    {t('agent.games.detail.downline.section', { label: dl.plural })}
                  </div>
                  <div className="rounded-2xl bg-white dark:bg-arena-700/40
                                  border border-arena-200/60 dark:border-arena-600/40
                                  divide-y divide-arena-100 dark:divide-white/8 overflow-hidden">
                    {d.downline.map((a) => (
                      <PersonRow key={a.agentId} icon="support_agent"
                                 label={<>{a.name} <span className={`font-medium ${T.faint}`}>#{a.agentId}</span></>}
                                 sub={<EquationLines parts={a.parts} />}
                                 amount={a.amount} />
                    ))}
                  </div>
                </div>
              )}

              {d.other !== 0 && (
                <div className="rounded-2xl bg-white dark:bg-arena-700/40
                                border border-arena-200/60 dark:border-arena-600/40 overflow-hidden">
                  <PersonRow icon="tune" label={t('agent.games.detail.other')} amount={d.other} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
